import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { app, db } from '../config/firebaseConfig'; // Import the centralized Firebase app
import { API_BASE_URL } from '../config/environment'; // Import the API base URL
import { debugLog } from '../utils/debug';
import { getImpersonationHeaders } from './ImpersonationContext';

// Get auth and provider from the centralized app
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

// Add scopes for Google account access
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Debug: Log Firebase auth status
// console.log('🔍 Firebase Auth Status:');
console.log('Firebase App:', app ? '✅ Initialized' : '❌ Not initialized');
console.log('Firebase Auth:', auth ? '✅ Available' : '❌ Not available');

const FirebaseAuthContext = createContext();

export const useFirebaseAuth = () => {
  const context = useContext(FirebaseAuthContext);
  if (!context) {
    throw new Error('useFirebaseAuth must be used within a FirebaseAuthProvider');
  }
  return context;
};

export const FirebaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminStatus, setAdminStatus] = useState({
    isAdmin: false,
    role: null,
    permissions: [],
    interclubId: null,
    interclubIds: [],
    termsVersion: null,
    termsAcceptedAt: null,
    termsRequiredVersion: null,
  });
  const [adminStatusLoading, setAdminStatusLoading] = useState(false);
  const [customClaims, setCustomClaims] = useState(null);
  
  // User profile with roles and approval status (from users collection, not adminUsers)
  const [userProfile, setUserProfile] = useState(null);

  // Function to load/reload user profile (supports impersonation)
  const loadUserProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUserProfile(null);
      return;
    }
    
    try {
      const profileToken = await firebaseUser.getIdToken();
      const impersonationHeaders = getImpersonationHeaders();
      const profileResponse = await fetch(
        `${API_BASE_URL.replace('/api', '')}/registration/profile`,
        { 
          headers: { 
            Authorization: `Bearer ${profileToken}`,
            ...impersonationHeaders
          } 
        }
      );
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserProfile(profileData.user || null);
        console.log('👤 User profile loaded:', {
          email: profileData.user?.email,
          roles: profileData.user?.roles,
          approvalStatus: profileData.user?.approvalStatus,
          interclubId: profileData.user?.interclubId,
          impersonating: !!impersonationHeaders['X-Impersonate-User'],
          impersonatedUserId: impersonationHeaders['X-Impersonate-User'],
          isInterclubAdmin: profileData.user?.roles?.includes('interclub_admin'),
          interclubAdminApproved: profileData.user?.approvalStatus?.interclubAdmin
        });
      } else {
        const errorText = await profileResponse.text();
        console.error('Failed to load user profile, status:', profileResponse.status, 'error:', errorText);
      }
    } catch (profileError) {
      console.error('Failed to load user profile:', profileError);
    }
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      if (!auth || !googleProvider) {
        throw new Error('Firebase is not properly configured. Please check your environment variables.');
      }

      setError(null);
      setLoading(true);
      
      const result = await signInWithPopup(auth, googleProvider);
      debugLog('Google sign-in successful:', result.user.email);
      
      // The user will be set via onAuthStateChanged
    } catch (error) {
      debugLog('Google sign-in failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUpWithEmail = async (email, password, firstName, lastName) => {
    try {
      if (!auth) {
        throw new Error('Firebase is not properly configured.');
      }

      setError(null);
      setLoading(true);
      
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      await updateProfile(result.user, {
        displayName: `${firstName} ${lastName}`
      });
      
      debugLog('Email sign-up successful:', result.user.email);
      
      // The user will be set via onAuthStateChanged
      return result.user;
    } catch (error) {
      debugLog('Email sign-up failed:', error);
      setError(error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email and password
  const signInWithEmail = async (email, password) => {
    try {
      if (!auth) {
        throw new Error('Firebase is not properly configured.');
      }

      setError(null);
      setLoading(true);
      
      const result = await signInWithEmailAndPassword(auth, email, password);
      debugLog('Email sign-in successful:', result.user.email);
      
      // The user will be set via onAuthStateChanged
    } catch (error) {
      debugLog('Email sign-in failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      if (!auth) {
        throw new Error('Firebase is not properly configured.');
      }
      if (!email) {
        throw new Error('Email is required to reset password.');
      }
      setError(null);
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      debugLog('Password reset failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      if (!auth) {
        throw new Error('Firebase is not properly configured.');
      }

      await signOut(auth);
      debugLog('User signed out successfully');
    } catch (error) {
      debugLog('Sign out failed:', error);
      setError(error.message);
    }
  };

  // Get Firebase ID token for API calls
  const getIdToken = async (forceRefresh = false) => {
    console.log('🔑 getIdToken called');
    console.log('👤 user:', user);
    console.log('🔐 auth:', auth);
    
    const authUser = auth.currentUser || user;
    if (!authUser) {
      
      return null;
    }
    
    if (!auth) {
      
      return null;
    }
    
    try {
      console.log('🔑 Attempting to get token from auth user...');
      const token = await authUser.getIdToken(forceRefresh);
      
      return token;
    } catch (error) {
      console.error('❌ Failed to get ID token from user object:', error);
      
      // Try alternative method using auth.currentUser
      try {
        console.log('🔄 Trying alternative method with auth.currentUser...');
        if (auth.currentUser) {
          const altToken = await auth.currentUser.getIdToken(true);
          
          return altToken;
        } else {
          
        }
      } catch (altError) {
        console.error('❌ Alternative token method also failed:', altError);
      }
      
      return null;
    }
  };

  const checkAdminFromFirestore = async (email) => {
    try {
      const directDoc = await getDoc(doc(db, 'adminUsers', email));
      if (directDoc.exists()) {
        const adminData = directDoc.data();
        if (adminData.isActive) {
          return {
            isAdmin: true,
            role: adminData.role,
            permissions: adminData.permissions || [],
            interclubId: adminData.interclubId || null,
            interclubIds: adminData.interclubIds || [],
            termsVersion: adminData.termsVersion || null,
            termsAcceptedAt: adminData.termsAcceptedAt || null,
            termsRequiredVersion: adminData.termsRequiredVersion || null,
            displayName: adminData.displayName,
          };
        }
      }

      const adminQuery = query(
        collection(db, 'adminUsers'),
        where('email', '==', email),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(adminQuery);
      if (!snapshot.empty) {
        const adminData = snapshot.docs[0].data();
        return {
          isAdmin: true,
          role: adminData.role,
          permissions: adminData.permissions || [],
          interclubId: adminData.interclubId || null,
          interclubIds: adminData.interclubIds || [],
          termsVersion: adminData.termsVersion || null,
          termsAcceptedAt: adminData.termsAcceptedAt || null,
          termsRequiredVersion: adminData.termsRequiredVersion || null,
          displayName: adminData.displayName,
        };
      }
    } catch (error) {
      console.error('❌ Firestore admin fallback failed:', error);
    }

    return {
      isAdmin: false,
      role: null,
      permissions: [],
      interclubId: null,
      interclubIds: [],
      termsVersion: null,
      termsAcceptedAt: null,
      termsRequiredVersion: null,
    };
  };

  // Check admin status and update state
  const checkAdminStatus = async (userToCheck = null) => {
    const userForCheck = userToCheck || user;
    if (!userForCheck?.email) {
      setAdminStatus({
        isAdmin: false,
        role: null,
        permissions: [],
        interclubId: null,
        interclubIds: [],
        termsVersion: null,
        termsAcceptedAt: null,
        termsRequiredVersion: null,
      });
      setAdminStatusLoading(false);
      return;
    }
    
    try {
      setAdminStatusLoading(true);
      const url = `${API_BASE_URL}/check-admin/${encodeURIComponent(userForCheck.email)}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAdminStatus({
          isAdmin: data.isAdmin,
          role: data.role,
          permissions: data.permissions || [],
          interclubId: data.interclubId || null,
          interclubIds: data.interclubIds || [],
          termsVersion: data.termsVersion || null,
          termsAcceptedAt: data.termsAcceptedAt || null,
          termsRequiredVersion: data.termsRequiredVersion || null,
        });
      } else {
        const fallback = await checkAdminFromFirestore(userForCheck.email);
        setAdminStatus(fallback);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      const fallback = await checkAdminFromFirestore(userForCheck.email);
      setAdminStatus(fallback);
    } finally {
      setAdminStatusLoading(false);
    }
  };

  // Check if user has admin role (synchronous, uses cached state)
  const isAdmin = () => {
    // If still loading, return false to prevent showing admin features prematurely
    if (adminStatusLoading) {
      return false;
    }
    
    // If impersonating another user, check if THAT user is an app admin
    // The impersonated user's admin status should be determined by their profile
    // not by the real admin's status. App admin comes from adminUsers collection
    // which the impersonated user likely doesn't have access to.
    const impersonationHeaders = getImpersonationHeaders();
    if (impersonationHeaders['X-Impersonate-User']) {
      // When impersonating, don't show app admin - that would be the real admin's status
      // The impersonated user's interclub admin status comes through userProfile
      return false;
    }
    
    return adminStatus.isAdmin;
  };

  // Check if user has super admin role
  const isSuperAdmin = () => {
    return adminStatus.role === 'SUPER_ADMIN';
  };

  // Check if user is an Interclub Admin in the adminUsers collection
  const isInterclubAdmin = () => {
    return adminStatus.role === 'INTERCLUB_ADMIN';
  };

  // Check if user has approved interclub_admin role from profile (role approval system)
  const isApprovedInterclubAdmin = () => {
    if (!userProfile) {
      console.log('🔍 isApprovedInterclubAdmin: userProfile is null');
      return false;
    }
    const hasRole = userProfile.roles?.includes('interclub_admin');
    const isApproved = userProfile.approvalStatus?.interclubAdmin === 'approved';
    console.log('🔍 isApprovedInterclubAdmin check:', { hasRole, isApproved, roles: userProfile.roles, approvalStatus: userProfile.approvalStatus });
    return hasRole && isApproved;
  };

  // Get the user's interclub ID (from profile for approved interclub admins)
  const getUserInterclubId = () => {
    // First check profile (for approved interclub admins from role approval system)
    if (userProfile?.interclubId) {
      return userProfile.interclubId;
    }
    // Then check adminStatus (for admins in adminUsers collection)
    if (adminStatus.interclubId) {
      return adminStatus.interclubId;
    }
    return null;
  };

  const getInterclubAdminIds = () => {
    if (Array.isArray(adminStatus.interclubIds) && adminStatus.interclubIds.length > 0) {
      return adminStatus.interclubIds;
    }
    return adminStatus.interclubId ? [adminStatus.interclubId] : [];
  };

  // Get user role
  const getUserRole = () => {
    return adminStatus.isAdmin ? adminStatus.role?.toLowerCase() || 'admin' : 'parent';
  };

  const acceptAdminTerms = async () => {
    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/admin-users/accept-terms`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to accept terms');
      }

      const data = await response.json();
      setAdminStatus((prev) => ({
        ...prev,
        termsVersion: data.termsVersion || prev.termsVersion,
        termsAcceptedAt: data.termsAcceptedAt || prev.termsAcceptedAt,
        termsRequiredVersion: data.termsRequiredVersion || prev.termsRequiredVersion,
      }));
      return data;
    } catch (error) {
      console.error('❌ Accept terms failed:', error);
      throw error;
    }
  };

  // Legacy data auto-linking removed - now handled during Add Skater flow

  useEffect(() => {
    console.log('🔄 useEffect triggered, auth object:', auth);
    if (!auth) {
      
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 onAuthStateChanged triggered with user:', firebaseUser?.email || 'null');
      try {
        if (firebaseUser) {
          debugLog('Firebase auth state changed - user signed in:', firebaseUser.email);
          console.log('🔐 User signed in:', firebaseUser.email);
          
          // Get custom claims if available
          console.log('🔑 Getting ID token result...');
          const token = await firebaseUser.getIdTokenResult();
          console.log('🔑 Token claims:', token.claims);
          setCustomClaims(token.claims || null);
          
          console.log('👤 Setting user state...');
          setUser(firebaseUser);
          console.log('👤 User state set, now checking admin status...');
          
          // Check admin status for this user immediately
          // Pass the user data directly to avoid state timing issues
          console.log('🔄 Automatically checking admin status for new user...');
          try {
            await checkAdminStatus(firebaseUser);
            
          } catch (adminError) {
            console.error('❌ Automatic admin check failed:', adminError);
          }
          
          // Load user profile for roles and approval status
          await loadUserProfile(firebaseUser);
        } else {
          debugLog('Firebase auth state changed - user signed out');
          console.log('🚪 User signed out');
          setUser(null);
          setCustomClaims(null);
          setUserProfile(null);
          setAdminStatus({
            isAdmin: false,
            role: null,
            permissions: [],
            interclubId: null,
            interclubIds: [],
            termsVersion: null,
            termsAcceptedAt: null,
            termsRequiredVersion: null,
          });
        }
      } catch (error) {
        debugLog('Error in auth state change:', error);
        console.error('❌ Error in auth state change:', error);
        setError(error.message);
      } finally {
        
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for impersonation changes and reload user profile
  useEffect(() => {
    const handleImpersonationChange = (event) => {
      console.log('🔄 Impersonation changed:', event.detail);
      // Reload the user profile with the new impersonation state
      if (user) {
        console.log('👤 Reloading user profile due to impersonation change...');
        loadUserProfile(user);
      }
    };

    window.addEventListener('impersonation-changed', handleImpersonationChange);
    return () => window.removeEventListener('impersonation-changed', handleImpersonationChange);
  }, [user, loadUserProfile]);

  const value = {
    user,
    loading,
    error,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    signOutUser,
    getIdToken,
    isAdmin,
    isSuperAdmin,
    isInterclubAdmin,
    isApprovedInterclubAdmin,
    getUserInterclubId,
    getInterclubAdminIds,
    getUserRole,
    customClaims,
    adminStatus,
    adminStatusLoading,
    userProfile,
    checkAdminStatus,
    acceptAdminTerms,
    reloadUserProfile: () => loadUserProfile(user),
    clearError: () => setError(null)
  };

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
};

export default FirebaseAuthContext;
